import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  MenuItem,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Metric,
  MetricType,
  SchemeForm,
  SchemeFormWorkout,
  SchemeFormWorkoutSet,
  SchemeMetric,
  TrainingType,
} from '@strength/domain';
import { getDefaultSetMetrics, getMetricSpec } from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { sortByOrder } from '@strength/utils';
import { MdChevronRight } from '@teamworksdev/icons';
import { atom } from 'jotai';
import { useMemo, useState } from 'react';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '../../../../components/form/form';
import { CollectionFlag } from '../../../../components/icons/collection-flag';
import { Input } from '../../../../components/input/input';
import { MultiSelect } from '../../../../components/input/multi-select';
import { Sheet, Slot } from '../../../../components/sheet';
import {
  CustomSlot,
  MetricsSlot,
  RenderCellContentProps,
} from '../../../../components/sheet/slots';
import { TabPanel, TabTrigger, Tabs } from '../../../../components/tab/tab';
import { useViewer } from '../../../../context/viewer';
import { useMetrics } from '../../../../hooks/useMetrics';
import { MetricsSwitch } from '../../exercises/components/MetricsSwitch';
import {
  FocusOption,
  SchemeFormMutationFns,
  SchemeFormSelectionData,
} from '../types';
import { getContextMenuContent } from './WorkoutContextMenu';

type SchemeFormTab = 'details' | 'workouts';

export const SchemeSheetContent = ({
  form,
  isEdit,
  focusOptions,
  trainingTypeOptions,
}: {
  form: UseFormReturn<SchemeForm>;
  isEdit: boolean;
  focusOptions: FocusOption[];
  trainingTypeOptions: TrainingType[];
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const disableEdit = isEdit && !hasPermissions('library.schemes', 'edit');
  const disableCreate = !isEdit && !hasPermissions('library.schemes', 'create');
  const disableWrite = isEdit ? disableEdit : disableCreate;
  const { control, watch, setValue } = form;
  const [tab, setTab] = useState<SchemeFormTab>('details');

  const [trainingTypeId, metrics] = watch(['trainingTypeId', 'metrics']);
  const { getMetrics } = useMetrics(false);

  const metricOptions = useMemo(() => {
    return getMetrics();
  }, [getMetrics]);

  const {
    fields: workouts,
    append: onAddWorkout,
    remove: onRemoveWorkout,
    update: onUpdateWorkout,
  } = useFieldArray({
    control,
    name: 'workouts',
    rules: { minLength: 1 },
  });

  return (
    <>
      <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
        <TabTrigger label={t('details')} value="details" />
        <TabTrigger label={t('workouts')} value="workouts" />
      </Tabs>

      <TabPanel value={tab} tab="details" className="flex flex-col pt-2">
        <div className="flex flex-col space-y-4">
          <FormField
            name={'name'}
            control={control}
            disabled={disableWrite}
            render={({ field: { ref, ...field } }) => (
              <FormItem>
                <FormLabel>{t('name')}</FormLabel>
                <Input {...field} ref={ref} placeholder={t('enterName')} />
              </FormItem>
            )}
          />

          <FormField
            name={'trainingTypeId'}
            control={control}
            render={({ field: { value, onChange, disabled: _, ...field } }) => (
              <FormItem>
                <FormControl>
                  <TextField
                    label={t('type')}
                    select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    {...field}
                    disabled={disableWrite}
                  >
                    {trainingTypeOptions.map((value) => (
                      <MenuItem key={value.id} value={value.id}>
                        {value.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name={'focusIds'}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem className="flex flex-col">
                <MultiSelect
                  label={t('focus')}
                  disabled={disableWrite}
                  placeholder={t('selectFocus')}
                  selectedOptions={focusOptions.filter((f) =>
                    value.includes(f.id),
                  )}
                  options={focusOptions.filter(
                    (f) => f.trainingTypeId === trainingTypeId,
                  )}
                  onChange={(e) => {
                    onChange(e.map((f) => f.id));
                  }}
                  optionToName={(f) => f.name}
                />
              </FormItem>
            )}
          />

          <FormField
            name={'metrics'}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormLabel>{t('metrics')}</FormLabel>
                <MetricsSwitch
                  metricsOptions={metricOptions}
                  disabled={disableWrite}
                  selectedMetrics={
                    value
                      ? value.map((m) => {
                          return {
                            id: m.metricId,
                            type: m.metricType,
                            name: m.metricName,
                            sortOrder: m.sortOrder,
                          };
                        })
                      : []
                  }
                  setSelectedMetrics={(selectedMetrics) => {
                    // update metrics
                    onChange(
                      selectedMetrics.map((sm, sortOrder) => {
                        return {
                          id: v4(),
                          metricId: sm.id,
                          metricName: sm.name,
                          metricType: sm.type,
                          sortOrder,
                        };
                      }),
                    );
                    // filter out metrics that are not in the selectedMetrics
                    const filteredWorkouts = workouts.map((workout) => {
                      return {
                        sets: workout.sets.map((set) => {
                          return {
                            id: set.id,
                            metrics: set.metrics.filter((m) =>
                              selectedMetrics.find(
                                (sm) => sm.id === m.metricId,
                              ),
                            ),
                          };
                        }),
                      };
                    });
                    setValue('workouts', filteredWorkouts);
                  }}
                />
              </FormItem>
            )}
          />
        </div>
      </TabPanel>
      <TabPanel value={tab} tab="workouts">
        <div className="overflow-y-scroll h-[calc(100vh-340px)]">
          {workouts.map((workout, index) => {
            const sets = workout.sets;
            return (
              <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                <WorkoutSet
                  sets={sets}
                  metrics={metrics}
                  index={index}
                  disableWrite={disableWrite}
                  onUpdateWorkout={(newWorkout) =>
                    onUpdateWorkout(index, newWorkout)
                  }
                  onRemoveWorkout={() => {
                    onRemoveWorkout(index);
                  }}
                />
              </Box>
            );
          })}
        </div>
        <Button
          variant="text"
          sx={{ color: 'info.main', mt: 1 }}
          onClick={() => onAddWorkout(getNewWorkout())}
          disabled={!(metrics && metrics.length > 0) || disableWrite}
        >
          {t('addWorkout')}
        </Button>
      </TabPanel>
    </>
  );
};

const cellInputAtom = atom<string>('');

const WorkoutSet = ({
  sets,
  metrics,
  index,
  onUpdateWorkout,
  onRemoveWorkout,
  disableWrite,
}: {
  sets: SchemeFormWorkoutSet[];
  metrics: SchemeMetric[];
  index: number;
  onUpdateWorkout: (updatedWorkout: SchemeFormWorkout) => void;
  onRemoveWorkout: () => void;
  /** The sheet is in create mode (Create key) or edit mode (Edit key) denial. */
  disableWrite: boolean;
}) => {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const { getMetric } = useMetrics();
  const { t } = useTranslation();

  const mutationFns = {
    onAddSet: () => {
      const newSet = getNewSet(metrics);
      onUpdateWorkout({ sets: [...sets, newSet] });
    },
    onRemoveSet: (setId: string) => {
      const filteredSets = sets.filter((set) => set.id !== setId);
      onUpdateWorkout({ sets: filteredSets });
    },
    onEditSet: (editedSet: SchemeFormWorkoutSet) => {
      onUpdateWorkout({
        sets: sets.map((set) => (set.id === editedSet.id ? editedSet : set)),
      });
    },
  };

  return (
    <Accordion
      defaultExpanded={index === 0}
      sx={{
        width: '100%',
        borderWidth: 1,
        borderColor: greyscaleLight[300],
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={
          <MdChevronRight
            size={18}
            className="pointer-events-none transform rotate-90"
          />
        }
        sx={{
          display: 'flex',
          gap: 1,
          flexDirection: 'row-reverse',
          paddingLeft: '26px',
        }}
      >
        {t('workoutNum', { num: index + 1 })}
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Sheet
          {...getSlotsForWorkout({
            sets,
            metrics,
            mutationFns,
            getMetric,
            t,
            isReadOnly: disableWrite,
          })}
          onUndo={() => {}}
          onRedo={() => {}}
          onBulkClear={() => {}}
          getContextMenuContent={(selectionData) => {
            return getContextMenuContent(selectionData, mutationFns);
          }}
          onCopy={() => {}}
          onPaste={() => {}}
          onDragEnd={() => {}}
        />
        <Button
          variant="text"
          onClick={() => onRemoveWorkout()}
          disabled={disableWrite}
        >
          {t('removeWorkout')}
        </Button>
      </AccordionDetails>
    </Accordion>
  );
};

type SchemeSlot = Slot<SchemeFormSelectionData>;

export const getSlotsForWorkout = ({
  sets,
  metrics,
  mutationFns,
  getMetric,
  t,
  isReadOnly,
}: {
  sets: SchemeFormWorkoutSet[];
  metrics: SchemeMetric[];
  mutationFns: SchemeFormMutationFns;
  getMetric: (id: string) => Metric;
  t: I18nTranslator;
  isReadOnly: boolean;
}) => {
  let currentY = 0;

  const slots: SchemeSlot[] = [];

  const gridSubRows = 1;
  const hiddenColumnsCount = 0;
  const columnCount = metrics.length;

  slots.push(
    new MetricsSlot(
      isReadOnly,
      () => false,
      { x: 0, y: currentY },
      sortByOrder(metrics),
      sets,
      (edit, set) => {
        mutationFns.onEditSet({
          id: set.id,
          metrics: set.metrics.map((m) =>
            m.metricId === edit.metricId ? { ...m, ...edit } : m,
          ),
        });
      },
      cellInputAtom,
      (props) => <SetMetricCellContent {...props} context={{ getMetric }} />,
      {},
      gridSubRows,
      hiddenColumnsCount,
      getMetric,
      {
        rawDataFn: (rows, rowMetrics): SchemeFormSelectionData[] =>
          rows.map((set) => ({
            type: 'Set',
            set,
            metrics: rowMetrics,
          })),
      },
    ),
  );
  currentY += sets.length + 1;

  slots.push(
    new CustomSlot(
      { x: 0, y: currentY },
      columnCount,
      1,
      isReadOnly,
      () => (
        <Button
          variant="text"
          sx={{ height: '100%', width: '100%' }}
          onClick={mutationFns.onAddSet}
        >
          {t('addSet')}
        </Button>
      ),
      {
        editBehavior: {
          type: 'trigger',
          onEnter: mutationFns.onAddSet,
        },
      },
    ),
  );
  currentY += 1;

  return { slots, rowCount: currentY, columnCount, gridSubRows };
};

export const SetMetricCellContent = ({
  row,
  metric,
  context,
}: RenderCellContentProps<
  SchemeFormWorkoutSet,
  SchemeMetric,
  MetricType,
  {
    getMetric: (id: string) => Metric;
  }
>) => {
  // do I need to check for errors?
  const className = 'flex items-center w-full ml-2';

  const metricValue = row.metrics.find((m) => m.metricId === metric.metricId);
  const spec = getMetricSpec(context.getMetric(metric.metricId));
  const metricValueInput = metricValue?.input ?? '';
  const valueParseResult = spec.schema.safeParse(metricValueInput);

  const forCollectionFlag = metricValue?.forCollection && <CollectionFlag />;

  if (!valueParseResult.success) {
    const errors = valueParseResult.error.format()._errors.join(', ');
    const showTooltip = metricValueInput.length > 0;
    const innerContent = (
      <div className={className}>
        <span className="text-red-500">{metricValue?.input}</span>
        {forCollectionFlag}
      </div>
    );
    if (showTooltip) {
      return (
        <Tooltip arrow title={errors}>
          {innerContent}
        </Tooltip>
      );
    } else {
      return innerContent;
    }
  }

  const value = valueParseResult.data;

  return (
    <div className={className}>
      {spec.displayValue(value)}
      {forCollectionFlag}
    </div>
  );
};

const getNewWorkout = (): SchemeFormWorkout => {
  return {
    sets: [],
  };
};

const getNewSet = (metrics: SchemeMetric[]): SchemeFormWorkoutSet => {
  return {
    id: v4(),
    metrics: getDefaultSetMetrics(metrics),
  };
};
